import { useEffect, useRef } from 'react'

import { Card, CardAction, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ListCard } from '@/components/list-card'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'

import type { Thread } from '@/hooks/use-threads'

import { MessageSquare, Trash2, MoreHorizontal, Pencil } from 'lucide-react'

import {
  ThreadFileChips,
  ThreadLatestUpdateLine,
  ThreadObjectChips,
  ThreadUpdatesDigest,
} from './thread-glance'
import { RelativeTime } from './relative-time'
import { cn } from '@/lib/utils'

export interface ThreadCardProps {
  thread: Thread
  isAlreadyInChat: boolean
  isRenaming: boolean
  renameDraft: string
  isUpdating: boolean
  setRenameDraft: (v: string) => void
  startRename: (t: Thread) => void
  cancelRename: () => void
  commitRename: (t: Thread) => void | Promise<void>
  onOpen: () => void
  onOpenInChat: () => void
  onDelete: () => void
}

export function ThreadCard({
  thread,
  isAlreadyInChat,
  isRenaming,
  renameDraft,
  isUpdating,
  setRenameDraft,
  startRename,
  cancelRename,
  commitRename,
  onOpen,
  onOpenInChat,
  onDelete,
}: ThreadCardProps) {
  const renameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isRenaming) renameRef.current?.focus()
  }, [isRenaming])

  const title = thread.subject || 'Untitled thread'
  const stop = (e: React.MouseEvent) => e.stopPropagation()

  const header = (
    <>
      {isRenaming ? (
        <div data-slot="card-title" className="pointer-events-auto min-w-0">
          <Input
            ref={renameRef}
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={() => void commitRename(thread)}
            disabled={isUpdating}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') {
                e.preventDefault()
                void commitRename(thread)
              } else if (e.key === 'Escape') {
                cancelRename()
              }
            }}
            className="h-8 text-base font-semibold"
          />
        </div>
      ) : (
        <CardTitle className="min-w-0 truncate text-base leading-tight" title={title}>
          {title}
        </CardTitle>
      )}
      <CardAction className="pointer-events-auto">
        <div className="flex shrink-0 items-center gap-0.5" onClick={stop}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="iconXs"
                aria-label="Thread actions"
                disabled={isRenaming}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={stop}>
              <DropdownMenuItem
                disabled={isAlreadyInChat}
                onClick={() => {
                  if (!isAlreadyInChat) onOpenInChat()
                }}
              >
                <MessageSquare className="h-4 w-4" />
                {isAlreadyInChat ? 'Already in chat' : 'Open in chat'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => startRename(thread)}>
                <Pencil className="h-4 w-4" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardAction>
    </>
  )

  const body = (
    <div className="flex flex-col gap-2.5">
      {thread.summary ? (
        <p className="line-clamp-3 text-sm leading-snug text-muted-foreground">{thread.summary}</p>
      ) : (
        <p className="text-sm text-muted-foreground/80">No summary yet</p>
      )}

      <ThreadUpdatesDigest updates={thread.updates_digest} />

      <ThreadObjectChips
        objects={thread.objects}
        activity={thread.activity}
        objectCount={thread.object_count}
      />

      <ThreadFileChips files={thread.files} fileCount={thread.file_count} />

      <ThreadLatestUpdateLine latest={thread.latest_object_update} />
    </div>
  )

  const footer = (
    <div className="border-t border-border/40 pt-2.5 text-xs text-muted-foreground">
      <span className="tabular-nums">{thread.message_count}</span>{' '}
      {thread.message_count === 1 ? 'message' : 'messages'}
      <span className="mx-1.5 text-border" aria-hidden>
        ·
      </span>
      <RelativeTime value={thread.updated_at} prefix="Updated" />
    </div>
  )

  return (
    <ListCard
      onOpen={isRenaming ? undefined : onOpen}
      openAriaLabel={`Open thread ${title}`}
      header={header}
      body={body}
      footer={footer}
      className={cn(
        'group h-full',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
      )}
      footerClassName="mt-auto border-t-0 px-0"
    />
  )
}

export function ThreadsListSkeleton() {
  return (
    <PaneResponsiveCardGrid aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i}>
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-2">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="size-7 rounded-md" />
            </div>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
            <div className="flex gap-2 pt-1">
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="border-t border-border/40 pt-2">
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        </Card>
      ))}
    </PaneResponsiveCardGrid>
  )
}

export interface ThreadsEmptyStateProps {
  title: string
  description: string
  ctaLabel?: string
  onCta?: () => void
}

export function ThreadsEmptyState({ title, description, ctaLabel, onCta }: ThreadsEmptyStateProps) {
  return (
    <div className="flex min-h-[40dvh] flex-col items-center justify-center gap-3 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <MessageSquare className="h-6 w-6" />
      </div>
      <div className="space-y-1">
        <h3 className="text-base font-semibold">{title}</h3>
        <p className="max-w-md text-sm text-muted-foreground">{description}</p>
      </div>
      {ctaLabel && onCta ? (
        <div className="mt-2">
          <Button onClick={onCta}>{ctaLabel}</Button>
        </div>
      ) : null}
    </div>
  )
}
